import React, { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { PRODUCTS, CATEGORIES, BRANDS } from "../mock";
import { useAllProducts } from "../hooks/useProducts";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronRight, SlidersHorizontal } from "lucide-react";

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("cat") || "";
  const q = searchParams.get("q") || "";

  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sort, setSort] = useState("popular");
  const [showFilters, setShowFilters] = useState(false);
  const allProducts = useAllProducts();

  const filtered = useMemo(() => {
    let list = [...allProducts];
    if (cat === "occasion") {
      list = list.filter((p) => p.condition === "Occasion");
    } else if (cat) {
      list = list.filter((p) => p.category === cat);
    }
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(s) || p.brand.toLowerCase().includes(s) || p.category.includes(s)
      );
    }
    list = list.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);
    if (selectedBrands.length) list = list.filter((p) => selectedBrands.includes(p.brand));
    switch (sort) {
      case "price-asc": list.sort((a, b) => a.price - b.price); break;
      case "price-desc": list.sort((a, b) => b.price - a.price); break;
      case "discount": list.sort((a, b) => b.discount - a.discount); break;
      default: break;
    }
    return list;
  }, [allProducts, cat, q, priceRange, selectedBrands, sort]);

  const currentCategory = CATEGORIES.find((c) => c.slug === cat);

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) => prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]);
  };

  const resetFilters = () => {
    setPriceRange([0, 2000]);
    setSelectedBrands([]);
    setSort("popular");
    setSearchParams({});
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center text-sm text-neutral-600 gap-1">
          <Link to="/" className="hover:text-[#0b2960]">Accueil</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/boutique" className="hover:text-[#0b2960]">Boutique</Link>
          {currentCategory && (<>
            <ChevronRight className="h-4 w-4" />
            <span className="text-[#0b2960] font-semibold">{currentCategory.name}</span>
          </>)}
          {q && (<>
            <ChevronRight className="h-4 w-4" />
            <span className="text-[#0b2960] font-semibold">Recherche : “{q}”</span>
          </>)}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">
              {currentCategory ? currentCategory.name : q ? `Résultats pour “${q}”` : "Toute la boutique"}
            </h1>
            <p className="text-sm text-neutral-500 mt-1">{filtered.length} produit{filtered.length > 1 ? "s" : ""} trouvé{filtered.length > 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="lg:hidden border-[#0b2960] text-[#0b2960]">
              <SlidersHorizontal className="h-4 w-4 mr-1" /> Filtres
            </Button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[190px] h-10 border-neutral-300">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Popularité</SelectItem>
                <SelectItem value="price-asc">Prix : croissant</SelectItem>
                <SelectItem value="price-desc">Prix : décroissant</SelectItem>
                <SelectItem value="discount">Meilleures remises</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Filters */}
          <aside className={`${showFilters ? "block" : "hidden"} lg:block bg-white border border-neutral-200 rounded-lg p-5 h-fit sticky top-40`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0b2960]">Filtres</h3>
              <button onClick={resetFilters} className="text-xs text-[#e11d24] hover:underline">Réinitialiser</button>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-sm text-neutral-800 mb-3">Catégorie</h4>
              <ul className="space-y-1.5 text-sm">
                <li>
                  <button onClick={() => setSearchParams({})} className={`text-left w-full py-1 ${!cat ? "text-[#e11d24] font-semibold" : "text-neutral-700 hover:text-[#0b2960]"}`}>Toutes</button>
                </li>
                {CATEGORIES.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => setSearchParams({ cat: c.slug })} className={`text-left w-full py-1 ${cat === c.slug ? "text-[#e11d24] font-semibold" : "text-neutral-700 hover:text-[#0b2960]"}`}>{c.name}</button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-sm text-neutral-800 mb-3">Prix</h4>
              <Slider value={priceRange} min={0} max={2000} step={50} onValueChange={setPriceRange} className="my-4" />
              <div className="flex items-center justify-between text-xs text-neutral-600">
                <span>{priceRange[0]} €</span>
                <span>{priceRange[1]} €</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-neutral-800 mb-3">Marques</h4>
              <ul className="space-y-2 max-h-60 overflow-auto pr-2">
                {BRANDS.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <Checkbox id={`b-${b}`} checked={selectedBrands.includes(b)} onCheckedChange={() => toggleBrand(b)} />
                    <label htmlFor={`b-${b}`} className="text-sm text-neutral-700 cursor-pointer">{b}</label>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Products */}
          <div>
            {filtered.length === 0 ? (
              <div className="bg-white border border-dashed border-neutral-300 rounded-lg p-12 text-center">
                <p className="text-neutral-600">Aucun produit ne correspond à vos critères.</p>
                <Button onClick={resetFilters} className="mt-4 bg-[#0b2960] hover:bg-[#081e47]">Réinitialiser les filtres</Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
